import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Control, useForm, UseFormRegister } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { pushMessage, resetChat, selectMessages } from '../../../../app/store/reducers/ChatAiSlice'
import { v4 as uuidv4 } from 'uuid'
import { usePostN8NSendMessageMutation } from '../../../../app/api'
import { showAlert } from '../../../../app/store/reducers/AppSlice'

interface FormValues {
  message: string
}

interface AiChatContextProps {
  buttonVisible: boolean
  open: boolean
  size: 'large' | 'default'
  tab: 'chat' | 'overview'
  setTab: Dispatch<SetStateAction<'chat' | 'overview'>>
  loading: boolean
  indicator: boolean
  popupMessage: boolean
  flow: 'support' | 'analysis'
  setFlow: (flow: 'support' | 'analysis') => void
  messages: { text: string; type: 'input' | 'output'; time?: string }[]
  formValues: FormValues
  handleButtonVisible: (value: boolean) => void
  toggleDrawer: () => void
  toggleSize: () => void
  clearChat: () => void
  handleSendMessage: (data: FormValues) => Promise<void>
  register: UseFormRegister<FormValues>
  control: Control<FormValues>
  handleSubmit: ReturnType<typeof useForm<FormValues>>['handleSubmit']
  chatRef: React.RefObject<HTMLDivElement>
  buttonSlot: HTMLElement | null
  setButtonSlot: Dispatch<SetStateAction<HTMLElement | null>>
  buttonWidth: number
  setButtonWidth: Dispatch<SetStateAction<number>>
}

const AiChatContext = createContext<AiChatContextProps | undefined>(undefined)

export const AiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch()
  const messages = useAppSelector(selectMessages)
  const [buttonVisible, setButtonVisible] = useState(true)
  const [open, setOpen] = useState(false)
  const [size, setSize] = useState<'large' | 'default'>('default')
  const [tab, setTab] = useState<'chat' | 'overview'>('chat')
  const [loading, setLoading] = useState(false)
  const [indicator, setIndicator] = useState(false)
  const [popupMessage, setPopupMessage] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [flow, setFlow] = useState<'support' | 'analysis'>('support')
  const chatRef = useRef<HTMLDivElement | null>(null)
  const [buttonSlot, setButtonSlot] = useState<HTMLElement | null>(null)
  const [buttonWidth, setButtonWidth] = useState(0)

  const { handleSubmit, reset, register, watch, control } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { message: '' },
  })

  const formValues = watch()
  const [sendMessage] = usePostN8NSendMessageMutation()

  const handleButtonVisible = (value: boolean) => setButtonVisible(value)
  const toggleDrawer = () => setOpen((prev) => !prev)
  const toggleSize = () => setSize((prev) => (prev === 'default' ? 'large' : 'default'))
  const clearChat = () => {
    dispatch(resetChat())
    setSessionId()
  }

  const handleSendMessage = async ({ message }: FormValues) => {
    setLoading(true)
    dispatch(pushMessage({ text: message, type: 'input' }))
    reset()

    try {
      await sendMessage({
        body: {
          sessionId: JSON.stringify(localStorage.getItem('session-chat-ai')),
          message: JSON.stringify(message),
          flow: JSON.stringify(flow),
        },
      }).then((response) => {
        if ('data' in response) {
          const errors = response.data?.errors
          const firstError = Array.isArray(errors)
            ? errors[0]
            : Object.values(errors ?? {}).flat()[0]

          if (firstError) {
            dispatch(
              showAlert({
                message: firstError,
                type: 'error',
                timeout: 2000,
              }),
            )
          } else {
            dispatch(pushMessage({ text: response.data?.data?.output ?? '', type: 'output' }))
          }
        }
      })
    } finally {
      setLoading(false)
    }
  }

  const setSessionId = () => {
    const sessionId = uuidv4()

    localStorage.setItem('session-chat-ai', sessionId)
  }

  useEffect(() => {
    localStorage.getItem('session-chat-ai') || setSessionId()
  }, [])

  useEffect(() => {
    const container = chatRef.current

    if (!open && loading) {
      setOpen(true)
    }

    if (container) {
      setTimeout(() => container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' }), 100)
    }
  }, [loading])

  useEffect(() => {
    const container = chatRef.current

    if (open && container) {
      setIndicator(false)
      setPopupMessage(false)
      setInitialized(true)
      setTimeout(() => container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' }), 100)
    }
  }, [open])

  useEffect(() => {
    if (!open && initialized) {
      setIndicator(true)
      setPopupMessage(true)

      setTimeout(() => setPopupMessage(false), 10 * 1000)
    }
  }, [messages])

  const value: AiChatContextProps = {
    buttonVisible,
    open,
    size,
    tab,
    setTab,
    loading,
    indicator,
    popupMessage,
    flow,
    setFlow,
    messages,
    formValues,
    handleButtonVisible,
    toggleDrawer,
    toggleSize,
    clearChat,
    handleSendMessage,
    register,
    control,
    handleSubmit,
    chatRef,
    buttonSlot,
    setButtonSlot,
    buttonWidth,
    setButtonWidth,
  }

  return <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>
}

export const useAiChat = () => {
  const context = useContext(AiChatContext)

  if (!context) {
    throw new Error('useAiChat must be used within an AiChatProvider')
  }

  return context
}

// Зазор между кнопкой ассистента и содержимым строки, под которую она встаёт
const BUTTON_SLOT_GAP = 16

// Строка (пагинация таблицы), к которой кнопка ассистента прижимается правым краем и центром
// по кнопкам перелистывания ([data-ai-button-anchor]); справа резервируем замеренную ширину кнопки.
// Таблицы внутри строк других таблиц и в модалках слот не занимают
export const useAiButtonSlot = (enabled: boolean) => {
  const { buttonWidth, setButtonSlot } = useAiChat()
  const elementRef = useRef<HTMLElement | null>(null)
  const [registered, setRegistered] = useState(false)

  const ref = useCallback(
    (element: HTMLElement | null) => {
      const previous = elementRef.current
      const own = !!element && !element.parentElement?.closest('.ant-table, [role="dialog"]')

      elementRef.current = own ? element : null
      setRegistered(own)
      // снимаем только свой слот: новая таблица могла смонтироваться раньше, чем ушла прежняя
      setButtonSlot((current) => (own ? element : current === previous ? null : current))
    },
    [setButtonSlot],
  )

  if (!enabled) return {}

  return { ref, style: registered ? { paddingRight: buttonWidth + BUTTON_SLOT_GAP } : undefined }
}
